import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./PlanrTooltip.module.scss";

/**
 * Фирменная подсказка на весь сайт. Монтируется один раз в каркасе приложения
 * (`AppShell`) и слушает документ целиком — отдельные страницы её не заводят.
 *
 * Показывает два вида подсказок:
 *   - `data-tip` (+ `data-tip-note` второй строкой) — явные подсказки сетки плана и списков;
 *   - обычный `title` у любого элемента.
 *
 * Нативный `title` браузер показывает примерно через секунду и системным мелким
 * шрифтом. Поэтому на время наведения атрибут снимается с элемента (иначе поверх
 * фирменной всплыла бы и системная), а когда курсор уходит — возвращается на место:
 * React про это ничего не знает и следующую правку `title` запишет как обычно.
 *
 * Слушатели висят на документе с делегированием: элементов с подсказками сотни,
 * вешать обработчики на каждый — лишняя работа при каждой перерисовке.
 */

/** Пауза перед показом: меньше — подсказка мигает при проходе курсора по сетке */
const OPEN_DELAY = 140;

/** Отступ подсказки от курсора и от краёв окна */
const CURSOR_GAP = 14;
const VIEWPORT_GAP = 8;

/** Атрибуты, которыми элементы объявляют свою подсказку */
export const TIP_ATTR = "data-tip";
export const TIP_NOTE_ATTR = "data-tip-note";

/** Куда на время наведения прячется нативный title */
const HIDDEN_TITLE_ATTR = "data-planr-title";

interface TipState {
    text: string;
    note: string;
    x: number;
    y: number;
}

export const PlanrTooltip: React.FC = () => {
    const [tip, setTip] = useState<TipState | null>(null);
    const [position, setPosition] = useState<React.CSSProperties>({ visibility: "hidden" });
    const rootRef = useRef<HTMLDivElement>(null);
    const timer = useRef<number>(undefined);
    // во время перетаскивания подсказка только мешает — курсор занят жестом
    const suspended = useRef(false);
    /** Элемент, у которого сейчас спрятан title */
    const titled = useRef<Element | null>(null);

    /** Вернуть элементу спрятанный title. Если React за это время записал новый — не трогаем. */
    const restoreTitle = useCallback(() => {
        const element = titled.current;
        titled.current = null;

        if (!element) {
            return;
        }

        const title = element.getAttribute(HIDDEN_TITLE_ATTR);
        element.removeAttribute(HIDDEN_TITLE_ATTR);

        if (title !== null && !element.hasAttribute("title")) {
            element.setAttribute("title", title);
        }
    }, []);

    const hide = useCallback(() => {
        window.clearTimeout(timer.current);
        restoreTitle();
        setTip(null);
    }, [restoreTitle]);

    useEffect(() => {
        const onOver = (event: MouseEvent) => {
            const target = event.target as Element | null;
            // спрятанный title тоже ищем: иначе переход курсора на дочерний элемент
            // не нашёл бы подсказку родителя, у которого атрибут уже снят
            const source =
                target && target.closest ? target.closest(`[${TIP_ATTR}],[title],[${HIDDEN_TITLE_ATTR}]`) : null;

            // курсор ходит внутри того же элемента — подсказка уже показана или ждёт паузы
            if (source && source === titled.current) {
                return;
            }

            restoreTitle();

            let text: string | null = null;
            let note = "";

            if (source && source.hasAttribute(TIP_ATTR)) {
                text = source.getAttribute(TIP_ATTR);
                note = source.getAttribute(TIP_NOTE_ATTR) || "";
            } else if (source) {
                text = (source.getAttribute("title") || "").trim();

                // пустой title — это способ погасить подсказку родителя, показывать нечего
                source.setAttribute(HIDDEN_TITLE_ATTR, source.getAttribute("title") || "");
                source.removeAttribute("title");
                titled.current = source;
            }

            if (!source || !text) {
                window.clearTimeout(timer.current);
                setTip(null);
                return;
            }

            const rect = source.getBoundingClientRect();
            // по горизонтали — за курсором, по вертикали — под элементом:
            // так подсказка не дёргается, пока курсор ходит вдоль блока
            const next = { text, note, x: event.clientX, y: rect.bottom + 6 };

            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => {
                if (!suspended.current) {
                    setPosition({ visibility: "hidden" });
                    setTip(next);
                }
            }, OPEN_DELAY);
        };

        // курсор ушёл за пределы окна
        const onOut = (event: MouseEvent) => {
            if (!event.relatedTarget) {
                hide();
            }
        };

        const onDown = () => {
            suspended.current = true;
            hide();
        };

        const onUp = () => {
            suspended.current = false;
        };

        document.addEventListener("mouseover", onOver);
        document.addEventListener("mouseout", onOut);
        document.addEventListener("pointerdown", onDown, true);
        document.addEventListener("pointerup", onUp, true);
        document.addEventListener("keydown", hide, true);
        window.addEventListener("scroll", hide, true);

        return () => {
            hide();
            document.removeEventListener("mouseover", onOver);
            document.removeEventListener("mouseout", onOut);
            document.removeEventListener("pointerdown", onDown, true);
            document.removeEventListener("pointerup", onUp, true);
            document.removeEventListener("keydown", hide, true);
            window.removeEventListener("scroll", hide, true);
        };
    }, [hide, restoreTitle]);

    // у правого и нижнего краёв окна подсказка разворачивается внутрь экрана
    useLayoutEffect(() => {
        const element = rootRef.current;

        if (!tip || !element) {
            return;
        }

        const { width, height } = element.getBoundingClientRect();
        const left = Math.max(VIEWPORT_GAP, Math.min(tip.x + CURSOR_GAP, window.innerWidth - width - VIEWPORT_GAP));
        const top =
            tip.y + height + VIEWPORT_GAP <= window.innerHeight ? tip.y : Math.max(VIEWPORT_GAP, tip.y - height - 26);

        setPosition({ left, top, visibility: "visible" });
    }, [tip]);

    if (!tip) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className={styles.tooltip} style={position} ref={rootRef}>
            <div className={styles.text}>{tip.text}</div>
            {tip.note && <div className={styles.note}>{tip.note}</div>}
        </div>,
        document.body
    );
};
